import { useTranslation } from "react-i18next";
import { Avatar, Box, IconButton, Modal, Typography } from "@mui/material";
import CloseButtonIcon from "assets/icons/Close";
import CustomInput from "components/Input";
import CustomButton from "components/Button";
import useModalStore, { defaultUserInfo } from "store/modalStore";
import useIsTablet from "hooks/useIsTablet";
import showPreviewIcon from "utils/showPreviewImage";
import Profile from "assets/Profile_pic.png";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import TelegramIcon from "@mui/icons-material/Telegram";
import { useState } from "react";

interface UserWalletChangeModalProps {
  handleUserWalletCredit: (amount: number) => void;
  isLoading: boolean;
}

const UserWalletChangeModal = ({ handleUserWalletCredit, isLoading }: UserWalletChangeModalProps) => {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isUserWalletModalOpen, userInfo, changeUserInfo, closeModal } = useModalStore();
  const { id, email, telegram, telegramUsername, wallet, icon } = userInfo;
  const [amount, setAmount] = useState<string>("");

  const handleModalClose = () => {
    changeUserInfo(defaultUserInfo);
    closeModal();
  };

  return (
    <Modal
      open={isUserWalletModalOpen}
      onClose={handleModalClose}
      aria-labelledby="modal-check-domain-title"
      aria-describedby="modal-check-domain-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "32px 24px 24px 24px",
          bgcolor: "#FFFFFF",
          boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          width: "90%",
          maxWidth: "520px",
          height: "auto",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 16, right: 16, width: 32, height: 32 }}
          aria-label="modal close button"
          onClick={handleModalClose}
        >
          <CloseButtonIcon />
        </IconButton>
        <Typography
          id="modal-title"
          variant="h2"
          sx={{
            marginBottom: "14px",
            fontSize: "24px",
            fontWeight: 400,
            color: "#2E263D",
          }}
        >
          {t("fundUserWallet")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
          <Avatar src={`${icon ? showPreviewIcon(icon) : Profile}`} sx={{ width: 100, height: 100 }} />
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <PermIdentityIcon
                sx={{
                  color: "#6D6777",
                  width: "18px",
                  height: "18px",
                }}
              />
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: "400",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#6D6777",
                }}
              >
                {id}
              </Typography>
            </Box>
            {email && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <AlternateEmailIcon sx={{ color: "#6D6777", width: "18px", height: "18px" }} />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "500",
                    fontFamily: "var(--cygreRegular), sans-serif",
                  }}
                >
                  {email}
                </Typography>
              </Box>
            )}

            {(telegram || telegramUsername) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <TelegramIcon
                  sx={{
                    color: "#6D6777",
                    width: "18px",
                    height: "18px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "500",
                    fontFamily: "var(--cygreRegular), sans-serif",
                  }}
                >
                  {telegram || telegramUsername}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "24px" }}>
          <CustomInput
            required
            fullWidth
            id="currentBalance"
            value={wallet?.toFixed(2)}
            placeholder=""
            name="currentBalance"
            height="40px"
            label={t("currentBalance")}
            customBgColor="#F6F7FB"
            isReadOnly
          />
          <CustomInput
            required
            fullWidth
            id="editBalance"
            placeholder={t("enteramount")}
            name="editBalance"
            value={amount}
            height="40px"
            label={t("replenishmentamount")}
            customBgColor="#F6F7FB"
            onChange={(event) => {
              setAmount(event.target.value);
            }}
            onlyNumbersValues={true}
            autoFocus={isTablet ? false : true}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexDirection: isTablet ? "column-reverse" : "row",
            alignItems: "center",
          }}
        >
          <CustomButton
            width="100%"
            height={40}
            textButton={t("cancel")}
            customColor="#6D6777"
            customBorderColor="#C9D0D8"
            customHoverColor="transparent"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleModalClose}
          />
          <CustomButton
            width="100%"
            textButton={t("replenish")}
            height={40}
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            loading={isLoading}
            onClick={() => handleUserWalletCredit(Number(amount))}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default UserWalletChangeModal;
